/**
 * Squad behaviour inside a firefight (02 §9.4): who steadies whom, where cover is, where a
 * flanker should go, who runs for help, and the role each man picks given his temperament
 * and what the squad around him is already doing.
 */

import { bark } from '../alert';
import { type Ctx, isFighter } from '../ctx';
import { lineOfSight } from '../statics';
import { TACTICS } from '../tuning';
import type { Enemy, World } from '../types';
import { cellFree, nearestFree } from './movement';
import { EYE } from './perception';

export function officerNear(w: World, e: Enemy): boolean {
  for (const o of w.enemies) {
    if (o.kind !== 'officer' || !isFighter(o) || o.id === e.id) continue;
    if ((o.x - e.x) * (o.x - e.x) + (o.z - e.z) * (o.z - e.z) <= TACTICS.officerAura * TACTICS.officerAura) return true;
  }
  return false;
}

export function alliesInFight(w: World, e: Enemy): Enemy[] {
  const out: Enemy[] = [];
  for (const o of w.enemies) {
    if (o.id === e.id || o.mans !== null || o.state !== 'combat') continue;
    if ((o.x - e.x) * (o.x - e.x) + (o.z - e.z) * (o.z - e.z) <= TACTICS.squadRadius * TACTICS.squadRadius) out.push(o);
  }
  return out;
}

/** A solid object near `e` that he can put between himself and (fromX, fromZ). */
export function findCoverSpot(ctx: Ctx, e: Enemy, fromX: number, fromZ: number): [number, number] | null {
  let best: [number, number] | null = null;
  let bestD = Infinity;
  for (const c of ctx.statics.colliders) {
    if (!c.blocksBullets) continue;
    const cdx = c.x - e.x;
    const cdz = c.z - e.z;
    if (cdx * cdx + cdz * cdz > TACTICS.coverSearch * TACTICS.coverSearch) continue;
    // Stand on the far side of the object from the threat.
    let ax = c.x - fromX;
    let az = c.z - fromZ;
    const al = Math.sqrt(ax * ax + az * az);
    if (al < 0.01) continue;
    ax /= al;
    az /= al;
    const extent = (c.shape === 'circle' ? c.r : Math.abs(ax) * c.hx + Math.abs(az) * c.hz) + 0.9;
    const sx = c.x + ax * extent;
    const sz = c.z + az * extent;
    if (!cellFree(ctx.statics, sx, sz)) continue;
    if (lineOfSight(ctx.statics, fromX, fromZ, EYE, sx, sz, 1.5)) continue;
    const d = (sx - e.x) * (sx - e.x) + (sz - e.z) * (sz - e.z);
    if (d < bestD) {
      bestD = d;
      best = [sx, sz];
    }
  }
  return best;
}

/** A point off to one side of the ranger, so the flanker arrives across the line of fire. */
export function flankSpot(ctx: Ctx, e: Enemy): [number, number] | null {
  const r = ctx.world.ranger;
  let vx = e.x - r.x;
  let vz = e.z - r.z;
  const l = Math.sqrt(vx * vx + vz * vz);
  if (l < 0.01) return null;
  vx /= l;
  vz /= l;
  const def = ctx.statics.def;
  let best: [number, number] | null = null;
  let bestD = Infinity;
  for (const side of [1, -1]) {
    const px = Math.min(def.sizeX - 2, Math.max(2, r.x + -vz * side * TACTICS.flankRadius + vx * 2));
    const pz = Math.min(def.sizeZ - 2, Math.max(2, r.z + vx * side * TACTICS.flankRadius + vz * 2));
    const free = nearestFree(ctx.statics, px, pz, 3);
    if (!free) continue;
    const d = (free[0] - e.x) * (free[0] - e.x) + (free[1] - e.z) * (free[1] - e.z);
    if (d < bestD) {
      bestD = d;
      best = [free[0] + 0.5, free[1] + 0.5];
    }
  }
  return best;
}

/** Where a man running for help should go: a standing alarm post, else uninvolved comrades. */
export function chooseRunnerGoal(ctx: Ctx, e: Enemy): boolean {
  const w = ctx.world;
  let bestD = TACTICS.runnerReach * TACTICS.runnerReach;
  let found = false;
  for (const def of ctx.statics.def.props) {
    if (def.kind !== 'alarmPost') continue;
    if (w.props.find((p) => p.id === def.id)?.destroyed === true) continue;
    const d = (def.x - e.x) * (def.x - e.x) + (def.z - e.z) * (def.z - e.z);
    if (d < bestD) {
      bestD = d;
      e.spotX = def.x;
      e.spotZ = def.z + 1.2;
      e.runnerPost = def.id;
      found = true;
    }
  }
  if (found) return true;
  for (const o of w.enemies) {
    if (o.id === e.id || o.mans !== null) continue;
    if (o.state !== 'guard' && o.state !== 'patrol' && o.state !== 'return') continue;
    const d = (o.x - e.x) * (o.x - e.x) + (o.z - e.z) * (o.z - e.z);
    if (d < 12 * 12 || d >= bestD) continue;
    bestD = d;
    e.spotX = o.x;
    e.spotZ = o.z;
    e.runnerPost = -1;
    found = true;
  }
  return found;
}

/** Decide what this man does in the firefight. Temperament first, then what the squad lacks. */
export function chooseRole(ctx: Ctx, e: Enemy): void {
  const w = ctx.world;
  const allies = alliesInFight(w, e);
  e.tacticTicks = TACTICS.rethinkTicks + ctx.rng.int(TACTICS.rethinkTicks);
  const steadied = officerNear(w, e);

  // Someone goes for help while the alarm is still unraised - and only one at a time.
  if (w.runnerId === -1 && w.alert < 3 && !steadied) {
    const alone = allies.length === 0;
    let farthest = true;
    const r = w.ranger;
    const myD = (e.x - r.x) * (e.x - r.x) + (e.z - r.z) * (e.z - r.z);
    for (const a of allies) if ((a.x - r.x) * (a.x - r.x) + (a.z - r.z) * (a.z - r.z) > myD) farthest = false;
    const wantsToRun = e.persona === 'jumpy' || (e.persona === 'cautious' && alone) || (allies.length >= 2 && farthest && e.persona !== 'aggressive');
    if (wantsToRun && chooseRunnerGoal(ctx, e)) {
      w.runnerId = e.id;
      e.state = 'runner';
      e.path = [];
      bark(ctx, e, 'alarm');
      return;
    }
  }

  const flankers = allies.filter((a) => a.role === 'flank').length;
  const pinned = e.suppressTicks > 0;
  if (allies.length >= 1 && flankers === 0 && !pinned && (e.persona === 'aggressive' || (e.persona === 'steady' && allies.length >= 2))) {
    const spot = flankSpot(ctx, e);
    if (spot) {
      e.role = 'flank';
      e.spotX = spot[0];
      e.spotZ = spot[1];
      e.hasSpot = true;
      e.path = [];
      bark(ctx, e, 'flanking');
      return;
    }
  }
  if (e.persona === 'cautious' || e.persona === 'jumpy' || pinned) {
    const spot = findCoverSpot(ctx, e, w.ranger.x, w.ranger.z);
    if (spot) {
      if (e.role !== 'cover') bark(ctx, e, 'covering');
      e.role = 'cover';
      e.spotX = spot[0];
      e.spotZ = spot[1];
      e.hasSpot = true;
      e.peeking = false;
      e.path = [];
      return;
    }
  }
  e.role = e.persona === 'aggressive' ? 'advance' : 'hold';
  e.hasSpot = false;
}
